import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  IPriceLine,
  CandlestickSeries,
  LineSeries,
  UTCTimestamp,
  createSeriesMarkers,
} from 'lightweight-charts';
import { scoutApi, StructureBar, StructurePayload, StructureZone } from '../../services/api';

type Resolution = 'HOUR_4' | 'DAY' | 'WEEK';

const RESOLUTION_LABELS: Record<Resolution, string> = {
  HOUR_4: '4H',
  DAY: '1D',
  WEEK: '1W',
};

const TREND_BADGE_COLOR: Record<string, string> = {
  UPTREND_INTACT: '#2ed573',
  UPTREND_QUESTIONED: '#f1c40f',
  DOWNTREND_INTACT: '#ff4757',
  DOWNTREND_QUESTIONED: '#f1c40f',
  RANGE: '#95a5a6',
  UNCLEAR: '#95a5a6',
};

const ZONE_COLOR: Record<'DEMAND' | 'SUPPLY' | 'FLIP', string> = {
  DEMAND: '#2ed573',
  SUPPLY: '#ff4757',
  FLIP: '#f1c40f',
};

type EmaPeriod = 8 | 20 | 50;
const EMA_PERIODS: EmaPeriod[] = [8, 20, 50];
const EMA_COLOR: Record<EmaPeriod, string> = {
  8: '#ffffff',
  20: '#ffeb3b',
  50: '#ff4757',
};

function computeEma(bars: StructureBar[], period: number): { time: number; value: number }[] {
  if (bars.length === 0) return [];
  const k = 2 / (period + 1);
  let ema = bars[0].close;
  const out: { time: number; value: number }[] = [{ time: bars[0].time, value: ema }];
  for (let i = 1; i < bars.length; i++) {
    ema = bars[i].close * k + ema * (1 - k);
    out.push({ time: bars[i].time, value: ema });
  }
  return out;
}

const StructureChart: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const markersPrimitiveRef = useRef<any>(null);
  const zoneLinesRef = useRef<IPriceLine[]>([]);
  const retestLinesRef = useRef<IPriceLine[]>([]);
  const emaSeriesRef = useRef<Record<EmaPeriod, ISeriesApi<'Line'> | null>>({ 8: null, 20: null, 50: null });

  const [resolution, setResolution] = useState<Resolution>('HOUR_4');
  const [payload, setPayload] = useState<StructurePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Visibility toggles — default to a minimal view (EMAs + next retest only)
  const [showHH, setShowHH] = useState(false);
  const [showHL, setShowHL] = useState(false);
  const [showLH, setShowLH] = useState(false);
  const [showLL, setShowLL] = useState(false);
  const [showDemand, setShowDemand] = useState(false);
  const [showSupply, setShowSupply] = useState(false);
  const [showFlip, setShowFlip] = useState(false);
  const [showRetest, setShowRetest] = useState(true);
  const [showEma8, setShowEma8] = useState(true);
  const [showEma20, setShowEma20] = useState(true);
  const [showEma50, setShowEma50] = useState(true);

  // Fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    scoutApi.getStructure('NATURALGAS', resolution, 120)
      .then(p => { if (!cancelled) setPayload(p); })
      .catch(e => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'fetch failed');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [resolution]);

  // Effect 1: build chart + candle series whenever payload changes
  useEffect(() => {
    if (!containerRef.current || !payload || payload.bars.length === 0) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: '#0e0f12' }, textColor: '#bbb' },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.04)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.04)' },
      },
      rightPriceScale: { borderColor: 'rgba(255, 255, 255, 0.1)' },
      timeScale: { borderColor: 'rgba(255, 255, 255, 0.1)', timeVisible: true },
    });
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });
    candleSeries.setData(payload.bars.map(b => ({
      time: b.time as UTCTimestamp,
      open: b.open, high: b.high, low: b.low, close: b.close,
    })));
    candleSeriesRef.current = candleSeries;

    chart.timeScale().fitContent();

    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      markersPrimitiveRef.current = null;
      zoneLinesRef.current = [];
      retestLinesRef.current = [];
      emaSeriesRef.current = { 8: null, 20: null, 50: null };
    };
  }, [payload]);

  // Effect 2: swing markers (filtered by toggles)
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series || !payload) return;
    const shown: Record<string, boolean> = {
      HH: showHH, HL: showHL, LH: showLH, LL: showLL,
    };
    const swings = [...payload.highs, ...payload.lows].filter(s => shown[s.label] ?? true);
    const markers = swings.map(s => ({
      time: s.time as UTCTimestamp,
      position: s.kind === 'high' ? 'aboveBar' as const : 'belowBar' as const,
      color: s.label === 'HH' ? '#2ed573'
        : s.label === 'HL' ? '#26a69a'
        : s.label === 'LH' ? '#ff7043'
        : s.label === 'LL' ? '#ef5350'
        : '#95a5a6',
      shape: 'circle' as const,
      text: `${s.label} ${s.price.toFixed(4)}`,
      size: 1,
    }));
    if (markersPrimitiveRef.current) {
      markersPrimitiveRef.current.setMarkers(markers);
    } else {
      markersPrimitiveRef.current = createSeriesMarkers(series, markers);
    }
  }, [payload, showHH, showHL, showLH, showLL]);

  // Effect 3: zone price lines (DEMAND/SUPPLY/FLIP)
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series || !payload) return;
    zoneLinesRef.current.forEach(line => series.removePriceLine(line));
    zoneLinesRef.current = [];
    payload.zones.forEach((z) => {
      const visible =
        (z.zone_type === 'DEMAND' && showDemand) ||
        (z.zone_type === 'SUPPLY' && showSupply) ||
        (z.zone_type === 'FLIP' && showFlip);
      if (!visible) return;
      const color = ZONE_COLOR[z.zone_type] ?? '#f1c40f';
      const label = `${z.zone_type} ${z.touches}×`;
      const top = series.createPriceLine({
        price: z.zone_high, color, lineWidth: 1, lineStyle: 0,
        axisLabelVisible: true, title: label,
      });
      const bot = series.createPriceLine({
        price: z.zone_low, color, lineWidth: 1, lineStyle: 0,
        axisLabelVisible: true, title: ' ',
      });
      zoneLinesRef.current.push(top, bot);
    });
  }, [payload, showDemand, showSupply, showFlip]);

  // Effect 4: next-retest lines
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series || !payload) return;
    retestLinesRef.current.forEach(l => series.removePriceLine(l));
    retestLinesRef.current = [];
    if (payload.next_retest_zone && showRetest) {
      const nrz = payload.next_retest_zone;
      const top = series.createPriceLine({
        price: nrz.zone_high, color: '#5b8def', lineWidth: 1, lineStyle: 2,
        axisLabelVisible: true, title: 'Next retest',
      });
      const bot = series.createPriceLine({
        price: nrz.zone_low, color: '#5b8def', lineWidth: 1, lineStyle: 2,
        axisLabelVisible: true, title: ' ',
      });
      retestLinesRef.current.push(top, bot);
    }
  }, [payload, showRetest]);

  // Effect 5: EMAs (computed client-side from bars)
  const emaData = useMemo(() => {
    if (!payload) return null;
    return {
      8: computeEma(payload.bars, 8),
      20: computeEma(payload.bars, 20),
      50: computeEma(payload.bars, 50),
    } as Record<EmaPeriod, { time: number; value: number }[]>;
  }, [payload]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !payload || !emaData) return;
    const shown: Record<EmaPeriod, boolean> = { 8: showEma8, 20: showEma20, 50: showEma50 };

    EMA_PERIODS.forEach(period => {
      const wantVisible = shown[period];
      const existing = emaSeriesRef.current[period];

      if (wantVisible && !existing) {
        const series = chart.addSeries(LineSeries, {
          color: EMA_COLOR[period],
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        series.setData(emaData[period].map(p => ({ time: p.time as UTCTimestamp, value: p.value })));
        emaSeriesRef.current[period] = series;
      } else if (!wantVisible && existing) {
        chart.removeSeries(existing);
        emaSeriesRef.current[period] = null;
      } else if (wantVisible && existing) {
        existing.setData(emaData[period].map(p => ({ time: p.time as UTCTimestamp, value: p.value })));
      }
    });
  }, [payload, emaData, showEma8, showEma20, showEma50]);

  // Helper for legend rows
  const ToggleRow: React.FC<{
    on: boolean;
    onToggle: () => void;
    tagClass: string;
    tagLabel: string;
    children: React.ReactNode;
  }> = ({ on, onToggle, tagClass, tagLabel, children }) => (
    <button
      type="button"
      className={`structure-legend-row legend-toggle ${on ? 'on' : 'off'}`}
      onClick={onToggle}
      aria-pressed={on}
    >
      <span className={`structure-tag ${tagClass}`}>{tagLabel}</span>
      <span className="legend-toggle-text">{children}</span>
      <span className="legend-toggle-state">{on ? '✓' : '○'}</span>
    </button>
  );

  return (
    <div className="structure-tab">
      <div className="structure-controls">
        <span className="advisor-label">Timeframe:</span>
        {(Object.keys(RESOLUTION_LABELS) as Resolution[]).map(r => (
          <button
            key={r}
            type="button"
            className={`structure-tf-btn ${resolution === r ? 'active' : ''}`}
            onClick={() => setResolution(r)}
            disabled={loading}
          >
            {RESOLUTION_LABELS[r]}
          </button>
        ))}
        {payload && (
          <span
            className="structure-trend-badge"
            style={{
              background: `${TREND_BADGE_COLOR[payload.trend_state] || '#95a5a6'}22`,
              color: TREND_BADGE_COLOR[payload.trend_state] || '#95a5a6',
              borderColor: TREND_BADGE_COLOR[payload.trend_state] || '#95a5a6',
            }}
          >
            {payload.trend_state.replace(/_/g, ' ')}
          </span>
        )}
        {payload && (
          <span className="structure-price-chip">
            Current: <strong>${payload.current_price.toFixed(4)}</strong>
            {payload.atr_14 != null && (
              <span style={{ opacity: 0.6, marginLeft: 8 }}>ATR(14) {payload.atr_14.toFixed(4)}</span>
            )}
          </span>
        )}
      </div>

      {loading && <p className="advisor-hint">Loading structure…</p>}
      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      <div className="structure-main">
        <div ref={containerRef} className="structure-chart-container" />

        {payload && (
          <section className="structure-panel structure-legend structure-legend-sidebar">
            <h3 className="structure-panel-title">Legend &amp; layers</h3>
            <p className="advisor-hint" style={{ marginBottom: 8 }}>
              Click any row to show or hide that layer on the chart.
            </p>

            <div className="legend-group-label">Swings</div>
            <ToggleRow on={showHH} onToggle={() => setShowHH(v => !v)} tagClass="HH" tagLabel="HH">
              Higher high — uptrend continues
            </ToggleRow>
            <ToggleRow on={showHL} onToggle={() => setShowHL(v => !v)} tagClass="HL" tagLabel="HL">
              Higher low — uptrend pullback held above prior low
            </ToggleRow>
            <ToggleRow on={showLH} onToggle={() => setShowLH(v => !v)} tagClass="LH" tagLabel="LH">
              Lower high — bearish signal inside uptrend context
            </ToggleRow>
            <ToggleRow on={showLL} onToggle={() => setShowLL(v => !v)} tagClass="LL" tagLabel="LL">
              Lower low — downtrend continues
            </ToggleRow>

            <div className="legend-group-label">Zones</div>
            <ToggleRow on={showDemand} onToggle={() => setShowDemand(v => !v)} tagClass="DEMAND" tagLabel="DEMAND">
              Buyers repeatedly stepped in (green lines)
            </ToggleRow>
            <ToggleRow on={showSupply} onToggle={() => setShowSupply(v => !v)} tagClass="SUPPLY" tagLabel="SUPPLY">
              Sellers repeatedly rejected (red lines)
            </ToggleRow>
            <ToggleRow on={showFlip} onToggle={() => setShowFlip(v => !v)} tagClass="FLIP" tagLabel="FLIP">
              Role-swap zone — acted as both support and resistance
            </ToggleRow>
            <ToggleRow on={showRetest} onToggle={() => setShowRetest(v => !v)} tagClass="RETEST" tagLabel="RETEST">
              Next expected retest (dashed blue)
            </ToggleRow>

            <div className="legend-group-label">Moving averages</div>
            <ToggleRow on={showEma8} onToggle={() => setShowEma8(v => !v)} tagClass="EMA8" tagLabel="EMA 8">
              Fast EMA — short-term momentum
            </ToggleRow>
            <ToggleRow on={showEma20} onToggle={() => setShowEma20(v => !v)} tagClass="EMA20" tagLabel="EMA 20">
              Mid EMA — trend pullback reference
            </ToggleRow>
            <ToggleRow on={showEma50} onToggle={() => setShowEma50(v => !v)} tagClass="EMA50" tagLabel="EMA 50">
              Slow EMA — broader trend backbone
            </ToggleRow>
          </section>
        )}
      </div>

      {payload && (
        <div className="structure-side-grid">
          <section className="structure-panel">
            <h3 className="structure-panel-title">Higher highs sequence (newest first)</h3>
            <ul className="structure-list">
              {payload.highs.slice().reverse().map((s, i, arr) => {
                const prev = arr[i + 1];
                const cmp = prev
                  ? (s.price > prev.price ? `above prior at $${prev.price.toFixed(4)}` : `below prior at $${prev.price.toFixed(4)}`)
                  : null;
                return (
                  <li key={s.time + '_' + i}>
                    <span className={`structure-tag ${s.label}`}>{s.label}</span>
                    <strong>${s.price.toFixed(4)}</strong>
                    <span className="structure-meta">
                      ({s.bar_index_from_latest} bars ago{cmp ? ` — ${cmp}` : ''})
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="structure-panel">
            <h3 className="structure-panel-title">Higher lows sequence (newest first)</h3>
            <ul className="structure-list">
              {payload.lows.slice().reverse().map((s, i, arr) => {
                const prev = arr[i + 1];
                const cmp = prev
                  ? (s.price > prev.price ? `above prior at $${prev.price.toFixed(4)}` : `below prior at $${prev.price.toFixed(4)}`)
                  : null;
                return (
                  <li key={s.time + '_' + i}>
                    <span className={`structure-tag ${s.label}`}>{s.label}</span>
                    <strong>${s.price.toFixed(4)}</strong>
                    <span className="structure-meta">
                      ({s.bar_index_from_latest} bars ago{cmp ? ` — ${cmp}` : ''})
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="structure-panel">
            <h3 className="structure-panel-title">DEMAND zones nearby</h3>
            <ul className="structure-list">
              {payload.zones
                .filter((z: StructureZone) => z.zone_type === 'DEMAND')
                .slice(0, 4)
                .map((z, i) => (
                  <li key={`d_${i}`}>
                    <span className="structure-tag DEMAND">DEMAND</span>
                    <strong>${z.zone_low.toFixed(4)} – ${z.zone_high.toFixed(4)}</strong>
                    <span className="structure-meta">
                      ({z.touches} touches, last {z.newest_touch_age_bars} bars ago)
                    </span>
                  </li>
                ))}
              {payload.zones.filter(z => z.zone_type === 'DEMAND').length === 0 && (
                <li className="structure-meta">No demand zones in this window.</li>
              )}
            </ul>
          </section>

          <section className="structure-panel">
            <h3 className="structure-panel-title">SUPPLY zones nearby</h3>
            <ul className="structure-list">
              {payload.zones
                .filter((z: StructureZone) => z.zone_type === 'SUPPLY')
                .slice(0, 4)
                .map((z, i) => (
                  <li key={`s_${i}`}>
                    <span className="structure-tag SUPPLY">SUPPLY</span>
                    <strong>${z.zone_low.toFixed(4)} – ${z.zone_high.toFixed(4)}</strong>
                    <span className="structure-meta">
                      ({z.touches} touches, last {z.newest_touch_age_bars} bars ago)
                    </span>
                  </li>
                ))}
              {payload.zones.filter(z => z.zone_type === 'SUPPLY').length === 0 && (
                <li className="structure-meta">No supply zones in this window.</li>
              )}
            </ul>
          </section>

          {payload.next_retest_zone && (
            <section className="structure-panel structure-panel-wide">
              <h3 className="structure-panel-title">Next expected retest (dashed blue on chart)</h3>
              <p className="advisor-hint" style={{ margin: 0 }}>
                <strong>${payload.next_retest_zone.zone_low.toFixed(4)} – ${payload.next_retest_zone.zone_high.toFixed(4)}</strong>
                {' — '}
                {payload.next_retest_zone.interpretation}
              </p>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default StructureChart;
